import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  TextField,
  Chip,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  LinearProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Refresh as RefreshIcon,
  Circle as CircleIcon,
  Keyboard as KeyboardIcon,
  Speed as SpeedIcon,
  ElectricBolt as ElectricIcon,
  Co2 as CarbonIcon,
  Add as AddIcon,
  Rocket as RocketIcon,
  FolderSpecial as PortfolioIcon,
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { getCurrentSettlementPeriod, getSettlementPeriodTime } from '../../services/bmrsApi';
import portfolioService from '../../services/portfolioService';
import ScopeSwitcher from '../common/ScopeSwitcher';
import MarketSelector from '../common/MarketSelector';
import AdapterHealthStrip from './AdapterHealthStrip';
import CommodityTicker from './CommodityTicker';

export default function Header({ carbonVisible, onToggleCarbon }) {
  const { state, actions } = useApp();
  const location = useLocation();
  // The Market selector + Scope switcher belong to the legacy market-data
  // dashboard. On platform / observability routes we replace them with the
  // adapter-health traffic-light strip.
  const isPlatformRoute = (location.pathname || '').startsWith('/platform') ||
                          (location.pathname || '').startsWith('/observability');
  const currentPeriod = getCurrentSettlementPeriod();
  const periodTime = getSettlementPeriodTime(currentPeriod);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [shortcutsAnchor, setShortcutsAnchor] = useState(null);
  const [showFirstRunDialog, setShowFirstRunDialog] = useState(false);
  const [creatingPortfolio, setCreatingPortfolio] = useState(false);

  // Check for first run on mount. Skip on Platform / Observability routes —
  // those don't need a portfolio (they run on the algo-platform backend, not
  // the legacy market-data pages).
  useEffect(() => {
    const path = window.location.pathname || '';
    const isPlatformRoute = path.startsWith('/platform') || path.startsWith('/observability');
    if (!isPlatformRoute && portfolioService.isFirstRun()) {
      const timer = setTimeout(() => setShowFirstRunDialog(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);

  // Handle creating sample portfolio
  const handleCreateSamplePortfolio = async () => {
    setCreatingPortfolio(true);
    try {
      const portfolio = portfolioService.createSamplePortfolio();
      if (portfolio) {
        actions.setScope('portfolio', portfolio.id, portfolio.name);
      }
      setShowFirstRunDialog(false);
    } catch (e) {
      console.error('Failed to create sample portfolio:', e);
    } finally {
      setCreatingPortfolio(false);
    }
  };

  // Handle skipping first run
  const handleSkipFirstRun = () => {
    portfolioService.markFirstRunComplete();
    setShowFirstRunDialog(false);
  };

  // Format last updated time
  const formatLastUpdated = () => {
    if (!state.lastUpdated) return null;
    const date = new Date(state.lastUpdated);
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  // Handle refresh click
  const handleRefresh = () => {
    setIsRefreshing(true);
    actions.triggerRefresh();
    // Reset refreshing state after animation
    setTimeout(() => setIsRefreshing(false), 1000);
  };

  // API rate limit percentage
  const apiRatePercent = (state.apiCalls.count / state.apiCalls.limit) * 100;
  const apiRateColor = apiRatePercent > 80 ? '#ff4444' : apiRatePercent > 50 ? '#ffaa00' : '#00ff88';

  // Keyboard shortcuts
  const shortcuts = [
    { key: 'R', description: 'Refresh data' },
    { key: 'S', description: 'Toggle sidebar' },
    { key: '1-5', description: 'Navigate to pages' },
    { key: '?', description: 'Show shortcuts' },
  ];

  return (
    <AppBar
      position="fixed"
      sx={{
        background: 'linear-gradient(135deg, rgba(15, 15, 35, 0.95) 0%, rgba(26, 26, 46, 0.95) 100%)',
        backdropFilter: 'blur(20px)',
        boxShadow: '0 4px 30px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
        borderBottom: '1px solid rgba(0, 212, 255, 0.1)',
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar sx={{ gap: 2 }}>
        {/* Menu Toggle */}
        <IconButton
          color="inherit"
          edge="start"
          onClick={actions.toggleSidebar}
          sx={{
            mr: 1,
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: 'rgba(0, 212, 255, 0.15)',
              color: '#00d4ff',
            },
          }}
        >
          <MenuIcon />
        </IconButton>

        {/* Logo */}
        <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1 }}>
          <ElectricIcon sx={{ color: '#00d4ff', fontSize: 28 }} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(90deg, #00d4ff, #00ff88)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '-0.5px',
            }}
          >
            EnergyCosmos
          </Typography>
        </Box>

        {/* Live Status */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.5,
            py: 0.5,
            borderRadius: 2,
            backgroundColor: 'rgba(0, 255, 136, 0.1)',
            border: '1px solid rgba(0, 255, 136, 0.2)',
          }}
        >
          <CircleIcon
            sx={{
              color: '#00ff88',
              fontSize: 10,
              animation: 'pulse 2s ease-in-out infinite',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.4 },
              },
            }}
          />
          <Typography variant="caption" sx={{ color: '#00ff88', fontWeight: 600, letterSpacing: 1 }}>
            LIVE
          </Typography>
        </Box>

        {/* Market Selector + Scope Switcher — legacy market-data only */}
        {!isPlatformRoute && <MarketSelector />}
        {!isPlatformRoute && <ScopeSwitcher />}

        {/* Adapter health traffic-light strip — platform routes only */}
        {isPlatformRoute && <AdapterHealthStrip />}

        {/* Live gas + carbon ticker — platform routes only.
            Feeds CCGT marginal-cost calc; trader can hide via the eye icon. */}
        {isPlatformRoute && <CommodityTicker />}

        {/* Current Settlement Period */}
        <Chip
          label={`Current: SP ${currentPeriod} (${periodTime.start})`}
          size="small"
          sx={{
            backgroundColor: 'rgba(0, 212, 255, 0.2)',
            color: '#00d4ff',
            border: '1px solid rgba(0, 212, 255, 0.3)',
          }}
        />

        {/* Last Updated */}
        {state.lastUpdated && (
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255,255,255,0.5)',
              display: { xs: 'none', md: 'block' },
            }}
          >
            Updated: {formatLastUpdated()}
          </Typography>
        )}

        {/* Spacer */}
        <Box sx={{ flex: 1 }} />

        {/* API Rate Limit Indicator */}
        <Tooltip title={`API Calls: ${state.apiCalls.count}/${state.apiCalls.limit} per minute`}>
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              backgroundColor: 'rgba(255,255,255,0.05)',
            }}
          >
            <SpeedIcon sx={{ fontSize: 16, color: apiRateColor }} />
            <Box sx={{ width: 60 }}>
              <LinearProgress
                variant="determinate"
                value={Math.min(apiRatePercent, 100)}
                sx={{
                  height: 4,
                  borderRadius: 2,
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: apiRateColor,
                    borderRadius: 2,
                  },
                }}
              />
            </Box>
            <Typography variant="caption" sx={{ color: apiRateColor, minWidth: 30 }}>
              {state.apiCalls.count}
            </Typography>
          </Box>
        </Tooltip>

        {/* Date Selector */}
        <TextField
          type="date"
          size="small"
          value={state.selectedDate}
          onChange={(e) => actions.setDate(e.target.value)}
          sx={{
            width: 160,
            display: { xs: 'none', sm: 'block' },
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'rgba(255,255,255,0.05)',
              color: 'white',
              '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
              '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
              '&.Mui-focused fieldset': { borderColor: '#00d4ff' },
            },
            '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
            '& input': {
              color: 'white',
              '&::-webkit-calendar-picker-indicator': {
                filter: 'invert(1)',
              },
            },
          }}
        />

        {/* Carbon Intensity Toggle */}
        <Tooltip title={carbonVisible ? "Hide carbon intensity" : "Show carbon intensity"}>
          <IconButton
            color="inherit"
            onClick={onToggleCarbon}
            sx={{
              transition: 'all 0.3s ease',
              backgroundColor: carbonVisible ? 'rgba(0, 255, 136, 0.15)' : 'transparent',
              color: carbonVisible ? '#00ff88' : 'inherit',
              '&:hover': {
                backgroundColor: 'rgba(0, 255, 136, 0.2)',
                color: '#00ff88',
              },
            }}
          >
            <CarbonIcon />
          </IconButton>
        </Tooltip>

        {/* Keyboard Shortcuts */}
        <Tooltip title="Keyboard shortcuts">
          <IconButton
            color="inherit"
            onClick={(e) => setShortcutsAnchor(e.currentTarget)}
          >
            <KeyboardIcon />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={shortcutsAnchor}
          open={Boolean(shortcutsAnchor)}
          onClose={() => setShortcutsAnchor(null)}
          PaperProps={{
            sx: {
              backgroundColor: '#1a1a2e',
              color: 'white',
              minWidth: 220,
            },
          }}
        >
          <Typography sx={{ px: 2, py: 1, fontWeight: 600, color: '#00d4ff' }}>
            Keyboard Shortcuts
          </Typography>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
          {shortcuts.map((shortcut) => (
            <MenuItem key={shortcut.key} dense sx={{ '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' } }}>
              <ListItemIcon>
                <Chip
                  label={shortcut.key}
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(0, 212, 255, 0.2)',
                    color: '#00d4ff',
                    fontFamily: 'monospace',
                    minWidth: 32,
                  }}
                />
              </ListItemIcon>
              <ListItemText
                primary={shortcut.description}
                primaryTypographyProps={{ variant: 'body2', color: 'rgba(255,255,255,0.8)' }}
              />
            </MenuItem>
          ))}
        </Menu>

        {/* Refresh Button */}
        <Tooltip title="Refresh Data (R)">
          <IconButton
            color="inherit"
            onClick={handleRefresh}
            sx={{
              transition: 'all 0.3s ease',
              animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
              '@keyframes spin': {
                from: { transform: 'rotate(0deg)' },
                to: { transform: 'rotate(360deg)' },
              },
              '&:hover': {
                backgroundColor: 'rgba(0, 212, 255, 0.15)',
                color: '#00d4ff',
              },
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Toolbar>

      {/* First Run Welcome Dialog */}
      <Dialog
        open={showFirstRunDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#1a1a2e',
            border: '1px solid rgba(0,212,255,0.3)',
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{
          textAlign: 'center',
          pt: 4,
          pb: 1,
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #00d4ff, #00ff88)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <RocketIcon sx={{ fontSize: 32, color: '#1a1a2e' }} />
            </Box>
            <Typography variant="h5" sx={{
              fontWeight: 700,
              background: 'linear-gradient(90deg, #00d4ff, #00ff88)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>
              Welcome to EnergyCosmos
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: 4, py: 2 }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.8)', textAlign: 'center', mb: 3 }}>
            Management-grade analytics for GB electricity trading. Track your portfolio performance
            across Day-Ahead, Intraday, and Balancing markets.
          </Typography>

          <Alert
            severity="info"
            icon={<PortfolioIcon />}
            sx={{
              backgroundColor: 'rgba(0,212,255,0.1)',
              border: '1px solid rgba(0,212,255,0.3)',
              color: 'rgba(255,255,255,0.9)',
              mb: 2,
              '& .MuiAlert-icon': { color: '#00d4ff' },
            }}
          >
            <Typography sx={{ fontSize: '0.85rem' }}>
              <strong>Get started instantly</strong> — We'll create a sample portfolio with 10 representative
              UK generators so you can explore all features right away.
            </Typography>
          </Alert>

          <Box sx={{
            p: 2,
            backgroundColor: 'rgba(0,255,136,0.05)',
            borderRadius: 2,
            border: '1px solid rgba(0,255,136,0.2)',
          }}>
            <Typography sx={{ color: '#00ff88', fontSize: '0.75rem', fontWeight: 600, mb: 1 }}>
              Sample Portfolio includes:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {['CCGT (4)', 'Wind (2)', 'Nuclear (1)', 'Biomass (1)', 'Pumped Storage (1)', 'Coal (1)'].map((item) => (
                <Chip
                  key={item}
                  label={item}
                  size="small"
                  sx={{
                    fontSize: '0.7rem',
                    height: 22,
                    backgroundColor: 'rgba(255,255,255,0.08)',
                    color: 'rgba(255,255,255,0.8)',
                  }}
                />
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 4, pb: 4, gap: 1, justifyContent: 'center' }}>
          <Button
            variant="text"
            onClick={handleSkipFirstRun}
            sx={{ color: 'rgba(255,255,255,0.5)' }}
          >
            Skip for now
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateSamplePortfolio}
            disabled={creatingPortfolio}
            startIcon={<AddIcon />}
            sx={{
              px: 4,
              py: 1.5,
              background: 'linear-gradient(135deg, #00d4ff, #00ff88)',
              color: '#1a1a2e',
              fontWeight: 700,
              '&:hover': {
                background: 'linear-gradient(135deg, #00b8e6, #00e077)',
              },
            }}
          >
            {creatingPortfolio ? 'Creating...' : 'Create Sample Portfolio'}
          </Button>
        </DialogActions>
      </Dialog>
    </AppBar>
  );
}
